import { Alert, Box, Button, FormControl, InputLabel, MenuItem, Select, TextField, Typography } from "@mui/material";
import React, { useRef, useState } from "react";
import Logger from "../../../services/Logger";
import DictionaryApiService from "../integrate/DictionaryApiService";

const DIC_LANGUAGES = [
    { value: "english", label: "English" },
    { value: "french", label: "French" },
];

export default function DicPlayWordMain() {
    const [word, setWord] = useState("");
    const [language, setLanguage] = useState("english");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [lastUrl, setLastUrl] = useState(null);
    const audioRef = useRef(null);

    const playAudioUrl = async (url) => {
        const el = audioRef.current;
        if (!el || !url) return;
        try {
            el.pause();
            el.src = url;
            el.load();
            await el.play();
        } catch (e) {
            Logger.error("Audio play failed:", e);
            setError(e?.message || "Could not play audio. Check browser autoplay / format support.");
        }
    };

    const handlePlay = async () => {
        const w = word.trim();
        if (!w) {
            setError("Enter a word to play.");
            return;
        }
        setLoading(true);
        setError(null);
        setLastUrl(null);
        try {
            const url = await DictionaryApiService.playWordAudio(w, language);
            if (!url) {
                setError("No audio URL returned.");
                return;
            }
            setLastUrl(url);
            await playAudioUrl(url);
        } catch (err) {
            Logger.error("Dic play word error:", err);
            const msg =
                err?.message ||
                (typeof err === "string" ? err : "Request failed. Check network and server logs.");
            setError(msg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, maxWidth: 560, width: "100%" }}>
            <Typography variant="body2" color="text.secondary">
                Calls <code>/home/dic/&#123;language&#125;/&#123;word&#125;/play</code> and plays the MP3 when a URL is
                returned.
            </Typography>

            <FormControl fullWidth size="small">
                <InputLabel id="dic-play-lang-label">Language</InputLabel>
                <Select
                    labelId="dic-play-lang-label"
                    label="Language"
                    value={language}
                    onChange={(e) => setLanguage(e.target.value)}
                >
                    {DIC_LANGUAGES.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            <TextField
                label="Word"
                value={word}
                onChange={(e) => setWord(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === "Enter") {
                        e.preventDefault();
                        handlePlay();
                    }
                }}
                fullWidth
                size="small"
                autoComplete="off"
            />

            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <Button variant="contained" onClick={handlePlay} disabled={loading}>
                    {loading ? "Loading…" : "Play"}
                </Button>
                {lastUrl ? (
                    <Typography variant="caption" color="text.secondary" sx={{ wordBreak: "break-all" }}>
                        {lastUrl}
                    </Typography>
                ) : null}
            </Box>

            {error ? <Alert severity="error">{error}</Alert> : null}

            <audio ref={audioRef} preload="none" style={{ width: "100%", marginTop: 8 }} controls />
        </Box>
    );
}
